import React from 'react';
import { fieldEvents, ATTEMPT_MAX_COUNT } from 'config/admin/event';
import {
  editFieldEventColumns,
  editTrackEventColumns,
  getResultHighJumpHeights,
} from 'config/admin/result';

import { convertMillisecondsToRecord } from 'utils/time';
import { sortByName } from 'utils/athleteOrder';

// The first height of a high jump event, in metres: the lowest height the
// results dialog accepts (0.50 to 2.50).
const FIRST_HIGH_JUMP_HEIGHT = 0.5;

export const getTableData = (results, eventType, iconStyle) => {
  if (results?.length) {
    const data = [];
    results.forEach((item) => {
      const transformedItem = {
        ...item,
        athleteName:
          item.athleteName ||
          `${item.athleteFirstName} ${item.athleteLastName}`,
        resultValue: item.resultRawValue
          ? convertMillisecondsToRecord(item.resultRawValue)
          : item.bestClearedHeight
            ? `${item.bestClearedHeight}`
            : 'NR',
        // For high jump, we need to ensure proper mapping
        resultID: item.resultID, // || item.athleteId, // Use athleteId as fallback for primary key
        athleteFirstName: item.athleteFirstName,
        athleteLastName: item.athleteLastName,
        athleteNumber: item.athleteNumber,
        // A high jump row names these age and gender (resultsToHighJumpTableData).
        athleteAge: item.athleteAge ?? item.age,
        athleteGender: item.athleteGender ?? item.gender,
        athleteID: item.athleteID,
      };

      // For high jump events, add proper height attempt mapping
      if (eventType === 'High Jump' && item.heightAttempts) {
        transformedItem.heightAttempts = item.heightAttempts.map((attempt) => ({
          ...attempt,
          // Ensure we have the right property names for the UI
        }));

        // Create the heightAttempts{index} properties that the UI expects
        item.heightAttempts.forEach((attempt, index) => {
          transformedItem[`heightAttempts${index}`] = attempt;
        });
      }

      data.push(transformedItem);
    });
    return data;
  } else {
    return [];
  }
};

export const getNewColumnTempData = (results, eventType) => {
  const columns = getColumns(results, eventType);
  const newRow = columns
    .map((c) => c.accessor)
    .reduce((acc, key) => {
      acc[key] = '';
      return acc;
    }, {});
  let tempData = {
    ...newRow,
    isNew: true,
    resultID: Date.now(), //temp
  };
  if (fieldEvents.includes(eventType)) {
    let attempts = [];
    if (results.length > 0) {
      for (let i = 0; i < results[0].attempts.length; i++) {
        attempts.push(
          <div
            style={{ paddingLeft: 15, minWidth: 70, paddingRight: 10 }}
            key={results.length}
          >
            0.00
          </div>
        );
        tempData[`attempt${i + 1}`] = newFieldEventResultAttempt(eventType);
      }
    } else {
      attempts.push(
        <div
          style={{ paddingLeft: 15, minWidth: 70, paddingRight: 10 }}
          key={results.length}
        >
          0.00
        </div>
      );
      tempData[`attempt1`] = newFieldEventResultAttempt(eventType);
    }
    tempData.attempts = attempts;
  } else if (eventType === 'High Jump') {
    // A new athlete gets one cell for every height already in the event. An
    // event with no results has no heights yet, so the first athlete gets one
    // starter height, or there would be nothing to enter or save (the same
    // idea as the single starter attempt for the other field events above).
    // The height is edited in the column heading; + Add Height adds more
    // once this athlete is saved.
    const existingHeights = getResultHighJumpHeights(results);
    const heightsArray = existingHeights.length
      ? existingHeights
      : [FIRST_HIGH_JUMP_HEIGHT];
    let highJumpAttempts = [];
    let heightAttempts = [];
    for (let i = 0; i < heightsArray.length; i++) {
      let newHeightAttempt = {
        resultId: 'new-' + (new Date().getTime() + i).toString(), // temp ID
        attempt1: '.',
        attempt2: '.',
        attempt3: '.',
        height: heightsArray[i],
        hasValidClear: true,
        timestamp: new Date().toISOString(),
      };
      highJumpAttempts.push(
        // add new heigh jump attempts
        <div style={{ paddingLeft: 20, minWidth: 70 }} key={i}>
          ...
        </div>
      );
      heightAttempts.push(newHeightAttempt);
      tempData[`heightAttempts${i}`] = newHeightAttempt;
    }
    tempData.highJumpAttempts = highJumpAttempts;
    tempData.heightAttempts = heightAttempts;
  }
  return tempData;
};

export const getColumnsWithAthleteListOption = (
  results,
  athletes,
  eventType
) => {
  const columns = getColumns(results, eventType);
  // An athlete already in the race, on a saved row or a new row already
  // chosen, is not offered again: the server refuses a second result for them
  // (Doug, 2026-10-23). A row shows its own athlete from the row, not from
  // this list, so leaving them out never blanks a row. A saved high jump row
  // holds the athlete as athleteId, as the server sends it for high jump
  // (resultsToHighJumpTableData); every other row as athleteID.
  const inRace = new Set(
    (results || []).map((row) => row.athleteID || row.athleteId).filter(Boolean)
  );
  const offered = athletes.filter(
    (at) => at.clubAthleteNumber && !inRace.has(at.athleteID)
  );

  // set athlete number options
  columns[0].options = offered.map((at) => at.clubAthleteNumber);

  // set athlete name options: by last name, then first name, then number
  columns[1].options = sortByName(
    offered,
    {
      lastName: (at) => at.lastName,
      firstName: (at) => at.firstName,
      number: (at) => at.clubAthleteNumber,
    }
  ).map((at) => ({
    athleteID: at.athleteID,
    clubAthleteNumber: at.clubAthleteNumber,
    firstName: at.firstName,
    lastName: at.lastName,
    gender: at.gender,
    ageGroup: at.ageGroup,
  }));
  return columns;
};

/**********************
 * Get Field Events Data for edit/delete
 */
export const getFieldEventResultsWithNewAttempt = (results, eventType) => {
  //  console.log('Input to getFieldEventResultsWithNewAttempt:', results.map(r => ({
  //   athleteID: r.athleteID,
  //   athleteFirstName: r.athleteFirstName,
  //   athleteLastName: r.athleteLastName,
  //   athleteNumber: r.athleteNumber
  // })));
  if (!results?.length) {
    return results;
  }
  if (results[0].attempts?.length >= ATTEMPT_MAX_COUNT) {
    return results;
  }

  const data = [];
  results.forEach((result) => {
    // result[`attempt${result.attempts.length + 1}`] = newAttempt;
    const attempts = [];
    result.attempts.forEach((at) => {
      attempts.push(at);
    });

    attempts.push(
      <div
        style={{ paddingLeft: 15, minWidth: 70, paddingRight: 10 }}
        key={results.length}
      >
        0.00
      </div>
    );

    data.push({
      ...result,
      resultValue: convertMillisecondsToRecord(result.resultRawValue),
      [`attempt${result.attempts.length + 1}`]:
        newFieldEventResultAttempt(eventType),
      attempts: attempts,
    });
  });

  //  console.log('Output from getFieldEventResultsWithNewAttempt:', data.map(r => ({
  //     athleteID: r.athleteID,
  //     athleteFirstName: r.athleteFirstName,
  //     athleteLastName: r.athleteLastName,
  //     athleteNumber: r.athleteNumber
  //   })));

  return data;
};

export const getfieldEventResultFinalAttemptsForDelete = (results) => {
  if (!results.length) {
    return [];
  }
  if (results[0].attempts.length <= 1) {
    // can't delete final attempt
    return [];
  }
  let attempts = [];
  results.forEach((result) => {
    attempts.push(result[`attempt${result.attempts.length}`]);
  });
  return attempts;
};

/**********************
 * Get high jump event data for edit/delete
 */
export const getHighJumpEventResultsWithNewAttempt = (results, eventType) => {
  if (!results?.length) {
    return results;
  }

  const data = [];
  const heighArrays = getResultHighJumpHeights(results);
  const newHeight =
    heighArrays.length > 0
      ? (+heighArrays[heighArrays.length - 1] + 0.1).toFixed(2)
      : FIRST_HIGH_JUMP_HEIGHT;
  results.forEach((result, index) => {
    let highJumpAttempts = [];
    let heightAttempts = [];
    result.highJumpAttempts.forEach((at) => {
      highJumpAttempts.push(at);
    });
    result.heightAttempts.forEach((at) => {
      heightAttempts.push(at);
    });
    highJumpAttempts.push(
      // add new heigh jump attempts
      <div style={{ paddingLeft: 20, minWidth: 70 }} key={index}>
        ...
      </div>
    );
    let newHeightAttempt = {
      resultId: 'new-' + (new Date().getTime() + index).toString(), // temp ID
      attempt1: '.',
      attempt2: '.',
      attempt3: '.',
      height: +newHeight,
      hasValidClear: true,
      isNew: true,
      timestamp: new Date().toISOString(),
    };
    heightAttempts.push(newHeightAttempt);
    data.push({
      ...result,
      heightAttempts: heightAttempts,
      highJumpAttempts: highJumpAttempts,
      [`heightAttempts${result.highJumpAttempts.length}`]: newHeightAttempt,
    });
  });
  return data;
};

// The warning before a result is deleted names the organisation's records: "club", or "school" for a school
// (Doug, 2026-10-27, school words pass 2).
const resultDeleteWarning = (organisationWord) =>
  `You won't be able to recover it once it has been deleted. Note: Approved ${organisationWord} records cannot be deleted.`;

export const deleteConfirmPopupUi = {
  result: {
    key: 'result',
    title: 'Delete Result',
    description: 'Do you really want to delete this result?',
    warningText: resultDeleteWarning('club'),
  },
  attempt: {
    key: 'attempt',
    title: 'Delete Attempt',
    description: 'Do you really want to delete this attempt for all athletes?',
    warningText: `The results for this attempt for all athletes will be permanently deleted and cannot be recovered!`,
  },
  height: {
    key: 'height',
    title: 'Delete Height',
    description: 'Do you really want to delete this height?',
    warningText: `All attempts at this height for all athletes will be permanently deleted and cannot be recovered!`,
  },
};

// The result's delete popup for the club on screen: a school reads "Approved school records cannot be deleted".
export const deleteResultPopupUi = (organisationWord = 'club') => ({
  ...deleteConfirmPopupUi.result,
  warningText: resultDeleteWarning(organisationWord),
});

const newFieldEventResultAttempt = (eventType) => {
  return {
    distance: 0,
    resultID: `new-${new Date().getTime()}`, // temp ID
    status: 'NR',
    isNew: true,
  };
};

const getColumns = (tableData, eventType) => {
  if (fieldEvents.includes(eventType) || eventType === 'High Jump') {
    return editFieldEventColumns(tableData, eventType);
  }
  return editTrackEventColumns;
};
